<template>
  <transition
    name="legend-slide"
    @enter="onEnter"
    @leave="onLeave"
  >
    <div
      v-if="shouldShowLegend"
      class="map-legend"
      :class="responsiveClasses"
      :style="legendStyles"
      role="complementary"
      aria-label="Map legend"
    >
      <!-- Crime Legend Section -->
      <div
        v-if="showCrimeLegend && crimeColorScale.length > 0"
        class="legend-section"
        :class="{ 'mb-4': showAgsLegend && agsColorScale.length > 0 }"
      >
        <!-- Crime Legend Header -->
        <div class="legend-header" :style="headerStyles">
          <div class="legend-title-container">
            <svg
              class="legend-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <h3 class="legend-title">Crime Data</h3>
          </div>
          <button
            class="legend-close-btn"
            :style="closeButtonStyles"
            @click="closeCrimeLegend"
            aria-label="Close crime legend"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <!-- Crime Legend Content -->
        <div class="legend-content">
          <!-- Comparison Context -->
          <div
            v-if="comparisonEnabled"
            class="comparison-context"
            :style="comparisonContextStyles"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 11l3 3L22 4"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span class="text-xs">
              Comparing to {{ comparisonScopeLabel }}
            </span>
          </div>

          <!-- Crime Color Scale -->
          <div class="color-scale">
            <div
              v-for="(item, index) in crimeColorScale"
              :key="`crime-${index}`"
              class="color-scale-item"
            >
              <div
                class="color-swatch"
                :style="{ backgroundColor: item.color }"
                :aria-label="`Risk level ${item.label}`"
              ></div>
              <span class="color-label">{{ item.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- AGS Legend Section -->
      <div
        v-if="showAgsLegend && agsColorScale.length > 0"
        class="legend-section"
      >
        <!-- AGS Legend Header -->
        <div class="legend-header" :style="headerStyles">
          <div class="legend-title-container">
            <svg
              class="legend-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 3v18h18"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M18 17l-5-5-4 4-6-6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <h3 class="legend-title">AGS Data</h3>
          </div>
          <button
            class="legend-close-btn"
            :style="closeButtonStyles"
            @click="closeAgsLegend"
            aria-label="Close AGS legend"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <!-- AGS Legend Content -->
        <div class="legend-content">
          <!-- Variable Count -->
          <div
            v-if="selectedAgsVariables.length > 0"
            class="variable-info"
            :style="variableInfoStyles"
          >
            <span class="text-xs font-medium">
              {{ selectedAgsVariables.length }} variable{{ selectedAgsVariables.length !== 1 ? 's' : '' }} selected
            </span>
          </div>

          <!-- AGS Color Scale -->
          <div class="color-scale">
            <div
              v-for="(item, index) in displayedAgsColorScale"
              :key="`ags-${index}`"
              class="color-scale-item"
            >
              <div
                class="color-swatch"
                :style="{ backgroundColor: item.color }"
                :aria-label="`Value ${item.label}`"
              ></div>
              <span class="color-label">{{ item.label }}</span>
            </div>
          </div>

          <!-- Reverse Colors Indicator -->
          <div
            v-if="reverseColors"
            class="reverse-indicator"
            :style="reverseIndicatorStyles"
          >
            <svg
              class="w-3 h-3"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 7v6h6M21 17v-6h-6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M21 3a16 16 0 00-12 6 16 16 0 0012 6M3 21a16 16 0 0112-6 16 16 0 01-12 6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span class="text-xs">Colors reversed</span>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ANIMATION_CONFIG, Z_INDEX, MAP_LEGEND_MAX_HEIGHT, BREAKPOINTS } from './constants.js';

export default {
  name: 'MapLegend',

  props: {
    // Crime legend data
    crimeColorScale: {
      type: Array,
      default: () => []
    },

    // AGS legend data
    agsColorScale: {
      type: Array,
      default: () => []
    },

    // Legend visibility flags
    showCrimeLegend: {
      type: Boolean,
      default: false
    },

    showAgsLegend: {
      type: Boolean,
      default: false
    },

    // Comparison mode settings
    comparisonEnabled: {
      type: Boolean,
      default: false
    },

    comparisonScope: {
      type: String,
      default: 'state',
      validator: (value) => ['state', 'county', 'viewport'].includes(value)
    },

    // Selected AGS variables
    selectedAgsVariables: {
      type: Array,
      default: () => []
    },

    // Color scale reversal
    reverseColors: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      windowWidth: window.innerWidth,
      maxHeight: MAP_LEGEND_MAX_HEIGHT
    };
  },

  computed: {
    /**
     * Determine if any legend should be shown
     */
    shouldShowLegend() {
      return (
        (this.showCrimeLegend && this.crimeColorScale.length > 0) ||
        (this.showAgsLegend && this.agsColorScale.length > 0)
      );
    },

    /**
     * Determine if the current viewport is mobile
     */
    isMobile() {
      return this.windowWidth < BREAKPOINTS.MOBILE;
    },

    /**
     * Responsive classes based on viewport size
     */
    responsiveClasses() {
      return {
        'map-legend--mobile': this.isMobile,
        'map-legend--desktop': !this.isMobile
      };
    },

    /**
     * Dynamic styles for the legend container
     */
    legendStyles() {
      return {
        backgroundColor: 'var(--background)',
        borderColor: 'var(--border)',
        color: 'var(--foreground)',
        zIndex: Z_INDEX.MAP_LEGEND,
        maxHeight: `${this.maxHeight}px`
      };
    },

    /**
     * Header styles
     */
    headerStyles() {
      return {
        borderBottomColor: 'var(--border)',
        background: 'linear-gradient(to bottom, rgba(var(--primary-rgb, 59, 130, 246), 0.05), transparent)'
      };
    },

    /**
     * Close button styles
     */
    closeButtonStyles() {
      return {
        color: 'var(--muted-foreground)'
      };
    },

    /**
     * Comparison context styles
     */
    comparisonContextStyles() {
      return {
        backgroundColor: 'rgba(var(--primary-rgb, 59, 130, 246), 0.1)',
        color: 'var(--primary)'
      };
    },

    /**
     * Variable info styles
     */
    variableInfoStyles() {
      return {
        backgroundColor: 'rgba(var(--muted-rgb, 100, 116, 139), 0.1)',
        color: 'var(--muted-foreground)'
      };
    },

    /**
     * Reverse indicator styles
     */
    reverseIndicatorStyles() {
      return {
        backgroundColor: 'rgba(var(--accent-rgb, 245, 158, 11), 0.1)',
        color: 'var(--accent)'
      };
    },

    /**
     * Get comparison scope label
     */
    comparisonScopeLabel() {
      const labels = {
        state: 'State Average',
        county: 'County Average',
        viewport: 'Visible Area'
      };
      return labels[this.comparisonScope] || 'State Average';
    },

    /**
     * Get AGS color scale with optional reversal
     */
    displayedAgsColorScale() {
      if (!this.agsColorScale || this.agsColorScale.length === 0) {
        return [];
      }

      // Return reversed copy if reverseColors is true
      if (this.reverseColors) {
        return [...this.agsColorScale].reverse();
      }

      return this.agsColorScale;
    }
  },

  mounted() {
    // Add resize listener for responsive behavior
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    // Clean up resize listener
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    /**
     * Handle window resize events
     */
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    /**
     * Close crime legend
     */
    closeCrimeLegend() {
      this.$emit('crime-legend-hidden');
    },

    /**
     * Close AGS legend
     */
    closeAgsLegend() {
      this.$emit('ags-legend-hidden');
    },

    /**
     * Animation enter hook
     */
    onEnter(el, done) {
      // Set initial state
      el.style.transform = 'translateY(100px)';
      el.style.opacity = '0';

      // Force reflow
      el.offsetHeight;

      // Animate to final state
      el.style.transition = `transform ${ANIMATION_CONFIG.LEGEND_SLIDE.duration}ms ease-out, opacity ${ANIMATION_CONFIG.LEGEND_SLIDE.duration}ms ease-out`;
      el.style.transform = 'translateY(0)';
      el.style.opacity = '1';

      // Call done after animation completes
      setTimeout(done, ANIMATION_CONFIG.LEGEND_SLIDE.duration);
    },

    /**
     * Animation leave hook
     */
    onLeave(el, done) {
      // Animate to hidden state
      el.style.transition = `transform 200ms ease-in, opacity 200ms ease-in`;
      el.style.transform = 'translateY(100px)';
      el.style.opacity = '0';

      // Call done after animation completes
      setTimeout(done, 200);
    }
  }
};
</script>

<style scoped>
.map-legend {
  position: absolute;
  bottom: 5rem; /* Moved up from 1.5rem to give more space */
  left: 1.5rem; /* right-6 */
  width: 12rem; /* w-80 */
  border-radius: 1rem; /* rounded-2xl */
  border-width: 2px;
  border-style: solid;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  overflow-y: auto;
  overflow-x: hidden;
  pointer-events: auto;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Dark mode support - background opacity adjustment */
:root[data-theme="dark"] .map-legend {
  background-color: rgba(0, 0, 0, 0.95);
}

:root[data-theme="light"] .map-legend {
  background-color: rgba(255, 255, 255, 0.95);
}

/* Legend Section */
.legend-section {
  padding: 1rem; /* p-4 */
}

/* Legend Header */
.legend-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.75rem; /* pb-3 */
  margin-bottom: 0.75rem; /* mb-3 */
  border-bottom-width: 1px;
  border-bottom-style: solid;
}

.legend-title-container {
  display: flex;
  align-items: center;
  gap: 0.5rem; /* gap-2 */
}

.legend-icon {
  width: 1.25rem; /* w-5 */
  height: 1.25rem; /* h-5 */
  flex-shrink: 0;
  color: var(--primary);
}

.legend-title {
  font-size: 0.875rem; /* text-sm */
  font-weight: 600;
  margin: 0;
  color: var(--foreground);
}

.legend-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem; /* p-1 */
  border-radius: 0.375rem; /* rounded-md */
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 200ms ease;
}

.legend-close-btn:hover {
  background-color: rgba(var(--accent-rgb, 245, 158, 11), 0.1);
  color: var(--accent);
}

.legend-close-btn:focus-visible {
  outline: 2px solid var(--ring);
  outline-offset: 2px;
}

/* Legend Content */
.legend-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem; /* gap-3 */
}

/* Comparison Context */
.comparison-context {
  display: flex;
  align-items: center;
  gap: 0.5rem; /* gap-2 */
  padding: 0.5rem 0.75rem; /* py-2 px-3 */
  border-radius: 0.5rem; /* rounded-lg */
  font-weight: 500;
}

/* Variable Info */
.variable-info {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem; /* py-2 px-3 */
  border-radius: 0.5rem; /* rounded-lg */
}

/* Color Scale */
.color-scale {
  display: flex;
  flex-direction: column;
  gap: 0.5rem; /* gap-2 */
}

.color-scale-item {
  display: flex;
  align-items: center;
  gap: 0.75rem; /* gap-3 */
}

.color-swatch {
  width: 2rem; /* w-8 */
  height: 1.5rem; /* h-6 */
  border-radius: 0.375rem; /* rounded-md */
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.color-label {
  font-size: 0.75rem; /* text-xs */
  color: var(--foreground);
  font-weight: 500;
}

/* Reverse Indicator */
.reverse-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem; /* gap-2 */
  padding: 0.5rem 0.75rem; /* py-2 px-3 */
  border-radius: 0.5rem; /* rounded-lg */
  font-weight: 500;
}

/* Responsive adjustments */
.map-legend--mobile {
  bottom: 1rem; /* bottom-4 */
  right: 1rem; /* right-4 */
  left: 1rem; /* left-4 */
  width: auto;
  max-height: 16rem; /* max-h-64 */
}

.map-legend--desktop {
  max-height: 24rem; /* max-h-96 */
}

/* Scrollbar styling */
.map-legend::-webkit-scrollbar {
  width: 0.5rem;
}

.map-legend::-webkit-scrollbar-track {
  background: transparent;
}

.map-legend::-webkit-scrollbar-thumb {
  background-color: rgba(var(--muted-rgb, 100, 116, 139), 0.3);
  border-radius: 0.25rem;
}

.map-legend::-webkit-scrollbar-thumb:hover {
  background-color: rgba(var(--muted-rgb, 100, 116, 139), 0.5);
}

/* Animation classes */
.legend-slide-enter-active,
.legend-slide-leave-active {
  transition: transform 400ms ease-out, opacity 400ms ease-out;
}

.legend-slide-enter,
.legend-slide-leave-to {
  transform: translateY(100px);
  opacity: 0;
}

/* Hover effect */
.map-legend:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Focus-visible for accessibility */
.map-legend:focus-within {
  outline: 2px solid var(--ring);
  outline-offset: 2px;
}

/* Animation for initial load */
@keyframes slideUp {
  from {
    transform: translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.map-legend {
  animation: slideUp 400ms ease-out;
}

/* Utility classes */
.mb-4 {
  margin-bottom: 1rem;
}

.w-4 {
  width: 1rem;
}

.h-4 {
  height: 1rem;
}

.w-3 {
  width: 0.75rem;
}

.h-3 {
  height: 0.75rem;
}

.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}

.font-medium {
  font-weight: 500;
}
</style>
